import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LandingSidebar } from '../components/LandingSidebar';
import {
    Activity,
    Brain,
    Calendar,
    Check,
    ChevronDown,
    ChevronRight,
    Heart,
    LineChart,
    MessageSquare,
    Moon,
    Pill,
    Shield,
    Sparkles,
    Users,
    Utensils,
    Zap,
    Stethoscope,
    ExternalLink,
    X,
    Loader2,
    Bell,
    Megaphone
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [waitlistName, setWaitlistName] = useState('');
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [waitlistSuccess, setWaitlistSuccess] = useState(false);
    const [waitlistError, setWaitlistError] = useState('');
    const [publicNews, setPublicNews] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/public/news`, { params: { limit: 3 } });
                setPublicNews(data || []);
            } catch (e) { /* silent */ }
        })();
    }, []);

    const handleWaitlistSubmit = async (e) => {
        e.preventDefault();
        setWaitlistLoading(true);
        setWaitlistError('');
        
        try {
            const response = await axios.post(`${API}/api/waitlist`, {
                name: waitlistName,
                email: waitlistEmail
            });
            setWaitlistSuccess(true);
            if (response.data.already_registered) {
                setWaitlistError("You're already on the waitlist!");
            }
        } catch (err) {
            setWaitlistError(err.response?.data?.detail || 'Failed to join waitlist. Please try again.');
        } finally {
            setWaitlistLoading(false);
        }
    };

    const features = [
        {
            icon: Activity,
            title: "Symptom Tracking",
            description: "Log and monitor your symptoms with severity ratings, triggers, and patterns over time."
        },
        {
            icon: Moon,
            title: "Sleep Analysis",
            description: "Track sleep quality, duration, and identify how rest impacts your condition."
        },
        {
            icon: Utensils,
            title: "Food Journal",
            description: "Document meals and discover food sensitivities that may trigger flares."
        },
        {
            icon: Pill,
            title: "Medication & Supplements",
            description: "Manage your treatment regimen with reminders and effectiveness tracking."
        },
        {
            icon: Brain,
            title: "AI Health Assistant",
            description: "Get personalized insights and answers from our AI trained on autoimmune health."
        },
        {
            icon: Users,
            title: "Community Support",
            description: "Connect with others on similar journeys. Share experiences and find encouragement."
        },
        {
            icon: LineChart,
            title: "Smart Correlations",
            description: "Discover hidden patterns between your symptoms, sleep, diet, and activities."
        },
        {
            icon: Calendar,
            title: "Medical Timeline",
            description: "Unified view of your complete health history for appointments and self-reflection."
        }
    ];

    const testimonials = [
        {
            name: "Sarah M.",
            condition: "Lupus",
            quote: "Finally, an app that understands the complexity of autoimmune conditions. The pattern detection helped me identify triggers I never knew I had.",
            avatar: "S"
        },
        {
            name: "Michael R.",
            condition: "Rheumatoid Arthritis",
            quote: "The AI chat feature is like having a knowledgeable friend available 24/7. It helped me prepare better questions for my rheumatologist.",
            avatar: "M"
        },
        {
            name: "Jennifer L.",
            condition: "Hashimoto's",
            quote: "Tracking my symptoms and correlating them with my diet and sleep has been eye-opening. I've reduced my flare frequency significantly.",
            avatar: "J"
        }
    ];

    const pricingTiers = [
        {
            name: "Essential",
            price: "$9",
            period: "per month",
            yearlyPrice: "$99/year",
            yearlySavings: "Save $9",
            description: "Complete tracking for health management",
            features: [
                "Symptom tracking & patterns",
                "Sleep & food logging",
                "Weather tracking",
                "Flare journal",
                "Knowledge base access",
                "Community forum",
                "Voice journaling",
                "AI health chat (limited)",
                "Cycle tracker"
            ],
            cta: "Choose Essential",
            popular: false
        },
        {
            name: "Premium",
            price: "$19",
            period: "per month",
            yearlyPrice: "$199/year",
            yearlySavings: "Save $29",
            description: "Complete toolkit for optimal health outcomes",
            features: [
                "Everything in Essential",
                "Unlimited AI health chat",
                "Smart correlations & insights",
                "Flare predictions",
                "Medical timeline",
                "Lab results tracking",
                "Supplement & medication tracker",
                "Medication reminders",
                "Appointment management",
                "PDF export & reports",
                "Weekly email summaries (Coming Soon)",
                "Priority support"
            ],
            cta: "Choose Premium",
            popular: true
        },
        {
            name: "Professional",
            price: "Coming Soon",
            period: "",
            yearlyPrice: "",
            yearlySavings: "",
            description: "Advanced features for comprehensive care",
            limitedSpots: true,
            features: [
                "Everything in Premium",
                "Async Messaging with Specialist",
                "Lab Interpretation (Educational)",
                "Personalized Educational Summaries",
                "Care Pathway Education",
                "Priority Access to New Tools",
                "Direct Support Channel"
            ],
            cta: "Join Waitlist",
            popular: false,
            comingSoon: true
        }
    ];

    return (
        <div className="flex min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
            {/* Sidebar Navigation */}
            <LandingSidebar />

            {/* Navigation - at root level for proper z-index stacking */}
            <nav className="fixed top-0 left-0 right-0 z-40 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800 lg:left-[72px]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 pl-12 lg:pl-0">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link to="/login">
                                <Button variant="ghost" className="text-slate-300 hover:text-white">
                                    Log In
                                </Button>
                            </Link>
                            <Link to="/plans">
                                <Button className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700">
                                    Get Started
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 overflow-x-hidden">

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                        Navigate Your Autoimmune Journey with{' '}
                        <span className="bg-gradient-to-r from-indigo-400 via-violet-400 to-purple-400 text-transparent bg-clip-text">
                            Confidence
                        </span>
                    </h1>
                    <p className="text-lg sm:text-xl text-slate-400 mb-8 max-w-3xl mx-auto">
                        Track symptoms, discover patterns, and take control of your health. 
                        Our platform helps you understand your body and make informed decisions 
                        about your autoimmune condition.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/plans">
                            <Button size="lg" className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-lg px-8 py-6 gap-2">
                                Start Your Journey
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                    <p className="mt-6 text-sm text-slate-500">
                        Cancel anytime · Upgrade whenever you need
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Everything You Need to Manage Your Health
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            Comprehensive tools designed specifically for the autoimmune community
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <Card key={index} className="bg-slate-800/50 border-slate-700 hover:border-indigo-500/50 transition-colors">
                                    <CardContent className="p-6">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 flex items-center justify-center mb-4">
                                            <Icon className="w-6 h-6 text-indigo-400" />
                                        </div>
                                        <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                                        <p className="text-sm text-slate-400">{feature.description}</p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Trusted by the Autoimmune Community
                        </h2>
                        <p className="text-lg text-slate-400">
                            Hear from real users managing their conditions with Autoimmune Pathways
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <Card key={index} className="bg-slate-800/50 border-slate-700">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-semibold">
                                            {testimonial.avatar}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-white">{testimonial.name}</p>
                                            <p className="text-sm text-slate-400">{testimonial.condition}</p>
                                        </div>
                                    </div>
                                    <p className="text-slate-300 italic">"{testimonial.quote}"</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Simple, Transparent Pricing
                        </h2>
                        <p className="text-lg text-slate-400">
                            Start free and upgrade when you're ready for more
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {pricingTiers.map((tier, index) => (
                            <Card key={index} className={`relative bg-slate-800/50 border-slate-700 ${tier.popular ? 'border-indigo-500 ring-2 ring-indigo-500/20 md:-mt-4 md:mb-4' : ''} ${tier.comingSoon ? 'opacity-80' : ''}`}>
                                {tier.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-indigo-500 text-white">Most Popular</Badge>
                                    </div>
                                )}
                                {tier.comingSoon && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-2">
                                        <Badge className="bg-amber-500 text-white">Coming Soon</Badge>
                                        {tier.limitedSpots && (
                                            <Badge className="bg-rose-500 text-white">Limited Spots</Badge>
                                        )}
                                    </div>
                                )}
                                <CardContent className="p-6">
                                    <h3 className="font-semibold text-xl text-white mb-2">{tier.name}</h3>
                                    <div className="mb-2">
                                        <span className={`text-4xl font-bold text-white ${tier.comingSoon ? 'text-2xl' : ''}`}>{tier.price}</span>
                                        {tier.period && <span className="text-slate-400">/{tier.period}</span>}
                                    </div>
                                    {tier.yearlyPrice && (
                                        <p className="text-sm text-emerald-400 mb-4">
                                            or {tier.yearlyPrice} <span className="text-emerald-300">({tier.yearlySavings})</span>
                                        </p>
                                    )}
                                    <p className="text-slate-400 text-sm mb-6">{tier.description}</p>
                                    <ul className="space-y-3 mb-6">
                                        {tier.features.map((feature, fIndex) => (
                                            <li key={fIndex} className="flex items-start gap-2 text-sm">
                                                <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                                                <span className="text-slate-300">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {tier.comingSoon ? (
                                        <Button 
                                            onClick={() => setShowWaitlistModal(true)}
                                            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                                        >
                                            {tier.cta}
                                        </Button>
                                    ) : (
                                        <Link to="/plans">
                                            <Button className={`w-full ${tier.popular ? 'bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700' : 'bg-slate-700 hover:bg-slate-600'}`}>
                                                {tier.cta}
                                            </Button>
                                        </Link>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

                        {/* Care Pathways Promo Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
                <div className="max-w-4xl mx-auto">
                    <Card className="bg-gradient-to-br from-teal-900/40 to-emerald-900/40 border-teal-700/50 overflow-hidden">
                        <CardContent className="p-8 md:p-12">
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                <div className="flex-shrink-0">
                                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/25">
                                        <Stethoscope className="w-10 h-10 text-white" />
                                    </div>
                                </div>
                                <div className="flex-1 text-center md:text-left">
                                    <Badge className="mb-4 bg-teal-500/20 text-teal-300 border-teal-500/30">
                                        Sister App
                                    </Badge>
                                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                                        Discover Care Pathways
                                    </h3>
                                    <p className="text-slate-300 mb-6 text-lg">
                                        Looking for the same support of Autoimmune Pathways but specific for PANDAS and PANS 
                                        and children with autoimmune disorders? Care Pathways was built specifically to provide 
                                        guidance and tracking from healthcare professionals who have lived with PANDAS in their 
                                        family and understand autoimmune disorders not just professionally but very personally as well.
                                    </p>
                                    <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-6">
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            PANDAS/PANS
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            Mycoplasma Pneumoniae
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            Histamine Intolerance
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            Mast Cell Activation
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            MTHFR Gene Mutation
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            Mold Exposures
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            Co-infections
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-sm">
                                            Parasites
                                        </span>
                                    </div>
                                    <a 
                                        href="https://www.calm-pathways.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-medium transition-all"
                                    >
                                        Learn More About Care Pathways
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-600 to-violet-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                        Ready to Take Control of Your Health?
                    </h2>
                    <p className="text-lg text-indigo-100 mb-8">
                        Join thousands of people managing their autoimmune conditions smarter.
                    </p>
                    <Link to="/plans">
                        <Button size="lg" className="bg-white text-indigo-600 hover:bg-slate-100 text-lg px-8 py-6 gap-2">
                            <Zap className="w-5 h-5" />
                            Choose Your Plan
                        </Button>
                    </Link>
                </div>
            </section>

            {/* What's New */}
            {publicNews.length > 0 && (
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900" data-testid="landing-whats-new">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center gap-2 text-indigo-400 text-sm font-semibold mb-2">
                                <Bell className="w-4 h-4" /> WHAT'S NEW
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-bold text-white">Latest from Autoimmune Pathways</h2>
                        </div>
                        <div className="grid gap-6 md:grid-cols-3">
                            {publicNews.map(p => {
                                const isNew = p.category === 'new_feature';
                                const Icon = isNew ? Sparkles : Megaphone;
                                const accent = isNew ? 'text-indigo-400' : 'text-amber-400';
                                const badgeBg = isNew ? 'bg-indigo-500' : 'bg-amber-500';
                                const badgeLabel = isNew ? 'New Feature' : 'Awareness';
                                return (
                                    <Card key={p.id} className="bg-slate-800 border-slate-700 hover:border-indigo-500 transition-colors" data-testid={`landing-news-card-${p.id}`}>
                                        <CardContent className="p-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Badge className={`${badgeBg} text-white text-[10px]`}>
                                                    <Icon className="w-3 h-3 mr-1" /> {badgeLabel}
                                                </Badge>
                                                <span className="text-[11px] text-slate-400">
                                                    {p.published_at ? new Date(p.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                                </span>
                                            </div>
                                            <h3 className={`text-lg font-semibold ${accent} mb-2`}>{p.title}</h3>
                                            <p className="text-sm text-slate-300 line-clamp-4 leading-relaxed">{p.body}</p>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-950 border-t border-slate-800">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-bold text-white">Autoimmune Pathways</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm flex-wrap justify-center">
                            <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                                Privacy Policy
                            </Link>
                            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                                Terms of Service
                            </Link>
                            <Link to="/faq" className="text-slate-400 hover:text-white transition-colors">
                                FAQs
                            </Link>
                            <Link to="/medical-disclaimer" className="text-slate-400 hover:text-white transition-colors">
                                Medical Disclaimer
                            </Link>
                            <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">
                                Contact
                            </Link>
                        </div>
                        <p className="text-sm text-slate-500">
                            © {new Date().getFullYear()} Autoimmune Pathways. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>

            {/* Waitlist Modal */}
            {showWaitlistModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <Card className="w-full max-w-md bg-slate-900 border-slate-700">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-white">Join the Waitlist</h3>
                                <button
                                    onClick={() => {
                                        setShowWaitlistModal(false);
                                        setWaitlistSuccess(false);
                                        setWaitlistError('');
                                        setWaitlistName('');
                                        setWaitlistEmail('');
                                    }}
                                    className="text-slate-400 hover:text-white"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            
                            {waitlistSuccess ? (
                                <div className="text-center py-4">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                        <Check className="w-8 h-8 text-emerald-400" />
                                    </div>
                                    <h4 className="text-lg font-semibold text-white mb-2">You're on the list!</h4>
                                    <p className="text-slate-400">
                                        We'll notify you as soon as the Professional tier is available.
                                    </p>
                                    <Button
                                        onClick={() => {
                                            setShowWaitlistModal(false);
                                            setWaitlistSuccess(false);
                                        }}
                                        className="mt-6 bg-indigo-500 hover:bg-indigo-600"
                                    >
                                        Got it!
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleWaitlistSubmit}>
                                    <p className="text-slate-400 mb-6">
                                        Be the first to know when our Professional tier launches with specialist messaging, 
                                        lab interpretation, and more.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="waitlist-name" className="text-slate-300">Name</Label>
                                            <Input
                                                id="waitlist-name"
                                                type="text"
                                                value={waitlistName}
                                                onChange={(e) => setWaitlistName(e.target.value)}
                                                placeholder="Your name"
                                                required
                                                className="mt-1 bg-slate-800 border-slate-700 text-white"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="waitlist-email" className="text-slate-300">Email</Label>
                                            <Input
                                                id="waitlist-email"
                                                type="email"
                                                value={waitlistEmail}
                                                onChange={(e) => setWaitlistEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                required
                                                className="mt-1 bg-slate-800 border-slate-700 text-white"
                                            />
                                        </div>
                                    </div>
                                    
                                    {waitlistError && (
                                        <p className="mt-4 text-sm text-amber-400">{waitlistError}</p>
                                    )}
                                    
                                    <Button
                                        type="submit"
                                        disabled={waitlistLoading}
                                        className="w-full mt-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                                    >
                                        {waitlistLoading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Joining...
                                            </>
                                        ) : (
                                            'Join Waitlist'
                                        )}
                                    </Button>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
            </div>
        </div>
    );
}
